import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Sparkles } from "lucide-react";

import { api, getErrorMessage, setAuthToken } from "@/api";

const labels = {
  manager: "Event manager",
  freelancer: "Freelancer",
};

export default function AuthPage({ onAuth }) {
  const navigate = useNavigate();
  const { role = "freelancer" } = useParams();
  const [mode, setMode] = useState("register");
  const [form, setForm] = useState({ name: "", email: "", password: "", company: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const update = (event) => {
    setForm({ ...form, [event.target.name]: event.target.value });
  };

  const submit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      const url = mode === "register" ? "/auth/register" : "/auth/login";
      const payload = mode === "register" ? { ...form, role } : form;
      const { data } = await api.post(url, payload);
      setAuthToken(data.token);
      localStorage.setItem("crewed-session", JSON.stringify(data));
      onAuth(data);
      navigate(role === "manager" ? "/manager" : "/jobs");
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  };

  return (
    <main className="auth-page" data-testid="auth-page">
      <Link className="back-link" data-testid="auth-back-link" to="/jobs">
        <ArrowLeft size={16} /> Back to opportunities
      </Link>
      <section className="auth-layout">
        <div className="auth-copy" data-testid="auth-intro">
          <p className="eyebrow"><Sparkles size={15} /> BUILT FOR LIVE MOMENTS</p>
          <h1>Find your next<br />great <em>gathering.</em></h1>
          <p>
            Join a curated network of people who make memorable events feel effortless.
          </p>
          <div className="auth-role-note" data-testid="auth-role-note">
            Joining as <strong>{labels[role] || labels.freelancer}</strong>
          </div>
        </div>
        <form className="auth-form" data-testid="auth-form" onSubmit={submit}>
          <div className="auth-switch" data-testid="auth-mode-switch">
            <button
              className={mode === "register" ? "active" : ""}
              data-testid="auth-register-tab"
              onClick={() => setMode("register")}
              type="button"
            >
              Create account
            </button>
            <button
              className={mode === "login" ? "active" : ""}
              data-testid="auth-login-tab"
              onClick={() => setMode("login")}
              type="button"
            >
              Sign in
            </button>
          </div>
          {mode === "register" && (
            <label data-testid="auth-name-label">
              Your name
              <input data-testid="auth-name-input" name="name" onChange={update} required />
            </label>
          )}
          {mode === "register" && role === "manager" && (
            <label data-testid="auth-company-label">
              Studio or company
              <input data-testid="auth-company-input" name="company" onChange={update} />
            </label>
          )}
          <label data-testid="auth-email-label">
            Email address
            <input data-testid="auth-email-input" name="email" onChange={update} required type="email" />
          </label>
          <label data-testid="auth-password-label">
            Password
            <input data-testid="auth-password-input" name="password" onChange={update} required type="password" />
          </label>
          {error && <p className="form-error" data-testid="auth-error-message">{error}</p>}
          <button className="primary-button auth-submit" data-testid="auth-submit-button" disabled={loading}>
            {loading ? "Working…" : mode === "register" ? "Join Crewed" : "Enter workspace"}
            <ArrowRight size={17} />
          </button>
          <p className="auth-role-link" data-testid="auth-role-link">
            {role === "manager" ? "Looking for work?" : "Hiring a crew?"}
            <Link data-testid="auth-switch-role-link" to={role === "manager" ? "/join/freelancer" : "/join/manager"}>
              Join as a {role === "manager" ? "freelancer" : "manager"}
            </Link>
          </p>
        </form>
      </section>
    </main>
  );
}